import { useEffect, useMemo, useState, useCallback } from "react";

import {
    getZcLoanCollateral,
    AbfLoanExpanded,
    getZcLedger,
    getPandoraErrorMessage,
    TokenRestrictedActions,
    isClmmMetadata,
    useUserTokens,
    BsMetaUtil
} from "@bridgesplit/abf-react";
import { WindType } from "@loopscale/pandora-types";
import { TagTextAlert, Column, Row, Text, Tooltip, Icon, RefreshButtonWithLoading, FONT_SIZES } from "@bridgesplit/ui";
import {
    BalanceDisplay,
    TokenInputWithPrice,
    AppButton,
    formatSlippageRowEndText,
    useLoanOraclePriceQuotes
} from "app/components/common";
import { TokenRestrictionAlerts } from "app/components/tokens/TokenRestrictionAlerts";
import debounce from "lodash.debounce";
import { filterTruthy, lamportsToUiAmount, Result } from "@bridgesplit/utils";
import { useTokenActionRestrictionsConfig, TokenRestrictionsConfig } from "app/hooks";
import type { TokenListMetadata } from "@bridgesplit/abf-sdk";
import { TransactionTypes } from "app/types/fees";
import { TEST_IDS } from "app/constants";
import { AppDialog, useAppDialog } from "app/utils/dialog";
import { useExecutionDetails } from "app/components/loop/execution-detail";
import { ExecutionDetailsLabels, LoopExecutionDetails } from "app/components/loop/execution-detail/types";

import { ActionsSettingsButton } from "../common";
import { usePrincipalRepayment } from "./hooks/usePrincipalRepayment";
import { MakeRepaymentProps } from "./types";
import { createRepaymentParams, useRepay } from "./util";
import {
    getExecutionDetailsInputs,
    useFullRepayWithCollateralQuote,
    useRepayWithCollateralQuote
} from "./hooks/manageLeverageHooks";
import { LoanPositionStats } from "../LoanPositionStats";

export interface RepaymentHandlerProps {
    // Token input
    inputValue: number | undefined;
    inputMetadata: TokenListMetadata | undefined;
    onInputChange: (amount: number | undefined) => void;
    repayLabel: string;

    // Balance below input (Total due)
    balanceLoading: boolean;
    totalDueInBalanceDisplay: number;
    balanceMetadata: TokenListMetadata | undefined;
    onSetMax: () => void;

    // RepayStats
    repaymentAmount: number | undefined;
    loading: boolean;

    // CTA & error
    error: string | null;
    ctaError: string | null;
    onRepay: () => Promise<Result<unknown>>;

    // Balance display (bottom)
    balance: number;

    // Token restrictions
    tokenRestrictionConfig: TokenRestrictionsConfig | undefined;
    isFullRepay: boolean;
    newCollateralAmount: number;
    newPrincipalAmount: number;

    executionDetails: LoopExecutionDetails | undefined;
}

export default function LoanRepayment({
    loanExpanded,
    slippageController,
    isNestedDialogView = false,
    selectedToken,
    setSelectedToken,
    loopPosition
}: MakeRepaymentProps) {
    const slippagePercentDecimals = slippageController.slippagePercentDecimals;
    const { fullRepayWithCollateralQuoteError, fullRepayWithCollateralQuoteLoading } = useFullRepayWithCollateralQuote({
        loanExpanded,
        slippagePercentDecimals
    });
    const { open } = useAppDialog();

    const principalMetadata = loanExpanded?.principalMetadata;
    const collateralMetadata = getZcLoanCollateral(loanExpanded)?.metadata;

    const supportedTokens = useMemo(
        () => getSupportedRepayTokens(fullRepayWithCollateralQuoteError, principalMetadata, collateralMetadata),
        [fullRepayWithCollateralQuoteError, principalMetadata, collateralMetadata]
    );

    const selectToken =
        supportedTokens.length > 1 && principalMetadata
            ? {
                  tokens: supportedTokens,
                  setToken: (token: TokenListMetadata) =>
                      setSelectedToken(token === principalMetadata ? WindType.Principal : WindType.Collateral),
                  header: ""
              }
            : null;

    const { handler } = useRepaymentSelect({
        loanExpanded,
        selectedToken,
        slippagePercentDecimals
    });

    const {
        inputValue, // needed
        inputMetadata, // needed
        onInputChange,
        balanceLoading,
        totalDueInBalanceDisplay,
        balanceMetadata,
        balance,
        repaymentAmount,
        loading,
        error,
        ctaError,
        onRepay,
        onSetMax,
        tokenRestrictionConfig,
        isFullRepay,
        newCollateralAmount,
        newPrincipalAmount,
        executionDetails // only the collateral repayment handler returns execution details
    } = handler;

    const isRepayWithCollateral = selectedToken === WindType.Collateral;

    return (
        <>
            <Column spacing={0.5}>
                {isRepayWithCollateral && (
                    <ActionsSettingsButton
                        label="Amount"
                        rowEndText={formatSlippageRowEndText(
                            slippageController.slippagePercentUi,
                            slippageController.slippagePercentDecimals
                        )}
                        onClick={() => {
                            if (isNestedDialogView) {
                                return slippageController.slippageButtonProps.open();
                            }
                            return open(AppDialog.SlippageSettings, slippageController.slippageDialogProps);
                        }}
                    />
                )}
                <TokenInputWithPrice
                    value={inputValue}
                    metadata={fullRepayWithCollateralQuoteLoading ? undefined : inputMetadata}
                    selectToken={selectToken}
                    supportedDepositTokens={supportedTokens}
                    setValue={onInputChange}
                    disabled={false}
                    belowInput={
                        isRepayWithCollateral ? (
                            <BalanceDisplay
                                isLoading={balanceLoading}
                                balance={totalDueInBalanceDisplay}
                                metadata={balanceMetadata}
                                setMax={onSetMax}
                                insufficientBalance={false}
                                maxText="Due"
                                hideRefresh
                                maxButtonCta="Max"
                            />
                        ) : (
                            <DueWithWalletRefresh
                                amount={totalDueInBalanceDisplay}
                                walletBalance={balance}
                                metadata={balanceMetadata}
                                onSetMax={onSetMax}
                            />
                        )
                    }
                />
            </Column>

            <Column spacing={0.5}>
                <LoanPositionStats
                    loopPosition={loopPosition}
                    loanExpanded={loanExpanded}
                    newPrincipalAmount={newPrincipalAmount}
                    newCollateralAmount={newCollateralAmount}
                    isFullRepay={isFullRepay}
                    loading={loading}
                    show={{
                        debtAndCollateral: true,
                        health: !!inputValue && !isFullRepay,
                        netApyStat: !!inputValue && !isFullRepay,
                        netApyCollapsable: false,
                        executionDetails: isRepayWithCollateral
                    }}
                    executionDetails={executionDetails}
                />
            </Column>
            <Column spacing={0.5}>
                <AppButton
                    dataTestId={TEST_IDS.REPAY_BUTTON}
                    isTransaction
                    transactionType={TransactionTypes.Repay}
                    disabled={!!ctaError || !inputValue || !repaymentAmount || loading}
                    asyncCta={{
                        onClickWithResult: onRepay,
                        closeDialog: "onSuccess",
                        options: {
                            onSuccess: () => onInputChange(undefined)
                        }
                    }}
                    fullWidth
                >
                    {ctaError || "Make Repayment"}
                </AppButton>

                {!!error && (
                    <TagTextAlert color="warning" textSx={{ display: "inline-block" }} icon="warning">
                        {error}
                    </TagTextAlert>
                )}
            </Column>
            <TokenRestrictionAlerts tokenRestrictionConfig={tokenRestrictionConfig} />
        </>
    );
}

function DueWithWalletRefresh({
    amount,
    walletBalance,
    metadata,
    onSetMax
}: {
    amount: number;
    walletBalance: number;
    metadata: TokenListMetadata | undefined;
    onSetMax: () => void;
}) {
    const { refresh, isLoading } = useUserTokens();
    return (
        <Row spaceBetween sx={{ alignItems: "center" }}>
            <Row spacing={0.5} sx={{ alignItems: "center" }}>
                <BalanceDisplay
                    isLoading={false}
                    balance={amount}
                    metadata={metadata}
                    insufficientBalance={false}
                    maxText="Due"
                    hideRefresh
                    hideRowEnd
                />
                <Tooltip
                    reverseColors
                    containerSx={{
                        display: "flex",
                        alignItems: "center",
                        "& svg": { fontSize: `${FONT_SIZES.body1}px` }
                    }}
                    title={
                        <Row spacing={0.5} sx={{ alignItems: "center" }}>
                            <Icon type="wallet" sx={{ fontSize: `${FONT_SIZES.body2}px` }} />
                            <Text variant="body2">{BsMetaUtil.formatAmount(metadata, walletBalance)}</Text>
                        </Row>
                    }
                >
                    <RefreshButtonWithLoading variant="body2" isFetching={isLoading} callback={() => void refresh()} />
                </Tooltip>
            </Row>
            {!!amount && (
                <Text
                    dataTestId={TEST_IDS.BALANCE_DISPLAY_MAX_BUTTON}
                    color="secondary"
                    variant="body2"
                    onClick={onSetMax}
                >
                    Max
                </Text>
            )}
        </Row>
    );
}

function getSupportedRepayTokens(
    fullRepayWithCollateralQuoteError: boolean,
    principalMetadata: TokenListMetadata | undefined,
    collateralMetadata: TokenListMetadata | undefined
): TokenListMetadata[] {
    if (!principalMetadata) return [];
    const isClmmCollateral = isClmmMetadata(collateralMetadata);
    if (fullRepayWithCollateralQuoteError || !collateralMetadata || isClmmCollateral) return [principalMetadata];
    return [principalMetadata, collateralMetadata];
}

function useRepaymentSelect({
    loanExpanded,
    selectedToken,
    slippagePercentDecimals
}: {
    loanExpanded: AbfLoanExpanded | undefined;
    selectedToken: WindType;
    slippagePercentDecimals: number | undefined;
}): { handler: RepaymentHandlerProps } {
    const principal = usePrincipalRepayment({ loanExpanded });
    const collateral = useCollateralRepaymentHandler({ loanExpanded, slippagePercentDecimals });

    const principalHandler: RepaymentHandlerProps = useMemo(
        () => ({
            inputValue: principal.inputAmount,
            inputMetadata: principal.principalMetadata,
            onInputChange: principal.handleInputChange,
            repayLabel: "Repay",
            balanceLoading: principal.balanceLoading,
            totalDueInBalanceDisplay: principal.totalDue ?? 0,
            balanceMetadata: principal.principalMetadata,
            onSetMax: principal.setMaxRepay,
            repaymentAmount: principal.effectiveInputAmount,
            loading: false,
            error: null,
            ctaError: principal.ctaError,
            onRepay: principal.submit,
            balance: principal.balance,
            tokenRestrictionConfig: undefined,
            newCollateralAmount: principal.newCollateralAmount,
            newPrincipalAmount: principal.newPrincipalAmount,
            isFullRepay: principal.isFullRepay,
            executionDetails: undefined
        }),
        [principal]
    );

    const collateralHandler: RepaymentHandlerProps = useMemo(
        () => ({
            inputValue: collateral.inputValue,
            inputMetadata: collateral.inputMetadata,
            onInputChange: collateral.onInputChange,
            repayLabel: collateral.repayLabel,
            balanceLoading: collateral.balanceLoading,
            totalDueInBalanceDisplay: collateral.totalDueInBalanceDisplay,
            balanceMetadata: collateral.balanceMetadata,
            onSetMax: collateral.onSetMax,
            repaymentAmount: collateral.repaymentAmount,
            loading: collateral.loading,
            error: collateral.error,
            ctaError: collateral.ctaError,
            onRepay: collateral.onRepay,
            balance: collateral.balance,
            tokenRestrictionConfig: collateral.tokenRestrictionConfig,
            newCollateralAmount: collateral.newCollateralAmount,
            newPrincipalAmount: collateral.newPrincipalAmount,
            isFullRepay: collateral.isFullRepay,
            executionDetails: collateral.executionDetails
        }),
        [collateral]
    );

    const handler = selectedToken === WindType.Principal ? principalHandler : collateralHandler;
    return { handler };
}

function useCollateralRepaymentHandler({
    loanExpanded,
    slippagePercentDecimals
}: {
    loanExpanded: AbfLoanExpanded | undefined;
    slippagePercentDecimals: number | undefined;
}) {
    const [repayWithCollateralAmount, setRepayWithCollateralAmount] = useState<number | undefined>();
    const [isFullRepay, setIsFullRepay] = useState(false);
    const [collateralUiAmount, setCollateralUiAmount] = useState<number | undefined>();
    const [hasPendingUpdate, setHasPendingUpdate] = useState(false);
    const collateral = getZcLoanCollateral(loanExpanded);

    const { collateralUsdOraclePrice } = useLoanOraclePriceQuotes(loanExpanded?.principalMetadata, collateral);

    const {
        repayWithCollateralQuote,
        repayWithCollateralQuoteLoading,
        repayWithCollateralQuoteFetching,
        repayWithCollateralQuoteError,
        customRepayAmountInPrincipalFromCollateralRepayment,
        validHealthRepayQuote
    } = useRepayWithCollateralQuote({
        loanExpanded,
        repayWithCollateralAmount,
        slippagePercentDecimals,
        skip: false
    });

    const { fullRepayAmountCollateralUi, fullRepayWithCollateralQuoteLoading, fullRepayWithCollateralQuoteError } =
        useFullRepayWithCollateralQuote({
            loanExpanded,
            slippagePercentDecimals
        });

    const ledger = getZcLedger(loanExpanded);

    const tokenRestrictionsConfig = useTokenActionRestrictionsConfig({
        mints: [collateral?.metadata?.mint].filter(filterTruthy),
        primaryRelevantAction: TokenRestrictedActions.WithdrawCollateral,
        isActive: true
    });

    const totalPrincipalDue = ledger?.ledgerDebt.total;
    const totalDueInCollateral = fullRepayAmountCollateralUi;

    const effectiveRepaymentAmount = isFullRepay // in principal
        ? totalPrincipalDue
        : repayWithCollateralAmount
        ? customRepayAmountInPrincipalFromCollateralRepayment
        : 0;

    const debouncedFormUpdate = useMemo(
        () =>
            debounce((amount: number | undefined, fullRepay: boolean) => {
                setRepayWithCollateralAmount(amount);
                setIsFullRepay(fullRepay);
                setHasPendingUpdate(false);
            }, 500),
        []
    );

    const setMaxRepay = useCallback(() => {
        debouncedFormUpdate.cancel();
        setIsFullRepay(true);
        setRepayWithCollateralAmount(fullRepayAmountCollateralUi);
        setCollateralUiAmount(fullRepayAmountCollateralUi);
        setHasPendingUpdate(false);
    }, [debouncedFormUpdate, fullRepayAmountCollateralUi]);

    useEffect(() => {
        return () => debouncedFormUpdate.cancel();
    }, [debouncedFormUpdate]);

    const params = createRepaymentParams({
        principalPaymentUiAmount: lamportsToUiAmount(
            repayWithCollateralQuote?.quote.minOutputAmount ?? 0,
            loanExpanded?.principalMetadata?.decimals
        ),
        collateralPaymentUiAmount: lamportsToUiAmount(
            repayWithCollateralQuote?.quote.inputAmount ?? 0,
            collateral?.metadata?.decimals
        ),
        isFullRepay,
        repayType: WindType.Collateral,
        collateral,
        loanExpanded,
        quote: repayWithCollateralQuote
    });

    const { repay } = useRepay(params);

    const handleInputChange = useCallback(
        (amt: number | undefined) => {
            const fullRepay = amt === totalDueInCollateral;
            setCollateralUiAmount(amt);
            setHasPendingUpdate(true);
            debouncedFormUpdate(amt, fullRepay);
        },
        [totalDueInCollateral, debouncedFormUpdate]
    );

    const error = useMemo(() => {
        if (fullRepayWithCollateralQuoteError) return "Repay with collateral unavailable.";
        if (!validHealthRepayQuote) return "Invalid health for repay with collateral quote";
        if (repayWithCollateralQuoteError) {
            return getPandoraErrorMessage(
                repayWithCollateralQuoteError,
                "Collateral quote unavailable for input amount. Please try a different amount."
            );
        }
        return null;
    }, [fullRepayWithCollateralQuoteError, repayWithCollateralQuoteError, validHealthRepayQuote]);

    const ctaError = useMemo(() => {
        if (tokenRestrictionsConfig?.activeRestrictionCta) return tokenRestrictionsConfig.activeRestrictionCta;
        const inputAmount = collateralUiAmount ?? repayWithCollateralAmount;
        if (fullRepayWithCollateralQuoteLoading || repayWithCollateralQuoteFetching) return "Loading";
        if (!inputAmount) return "Enter an amount";
        if (!isFullRepay && inputAmount > totalDueInCollateral) return "Exceeds total due";
        return null;
    }, [
        collateralUiAmount,
        repayWithCollateralAmount,
        isFullRepay,
        totalDueInCollateral,
        fullRepayWithCollateralQuoteLoading,
        repayWithCollateralQuoteFetching,
        tokenRestrictionsConfig?.activeRestrictionCta
    ]);

    const loading = repayWithCollateralQuoteLoading || hasPendingUpdate || fullRepayWithCollateralQuoteLoading;
    const disabledInput = fullRepayWithCollateralQuoteError || fullRepayWithCollateralQuoteLoading;
    const newCollateralAmount = (collateral?.amount ?? 0) - (repayWithCollateralAmount ?? 0);

    const executionDetailsInputs = getExecutionDetailsInputs({
        increaseLeverageQuote: undefined,
        repayWithCollateralQuote,
        repayWithCollateralAmount,
        collateral,
        increasePrincipalAmount: undefined,
        isIncreaseLeverage: false,
        principalUsdPrice: loanExpanded?.principalUsdPrice,
        collateralUsdPrice: collateral?.usdPrice,
        principalMetadata: loanExpanded?.principalMetadata
    });

    const executionDetails = useExecutionDetails({
        slippagePercentDecimals: slippagePercentDecimals,
        externalQuote: executionDetailsInputs?.externalQuote,
        receiveUsdAmount: executionDetailsInputs?.receiveUsdAmount,
        fairMarketValue: executionDetailsInputs?.fairMarketValue,
        labelOverrides: getLabelOverridesForCollateralRepayment(),
        principalUsdPrice: loanExpanded?.principalUsdPrice,
        collateralUsdPrice: collateralUsdOraclePrice,
        principalMetadata: loanExpanded?.principalMetadata,
        collateralMetadata: collateral?.metadata
    });

    return {
        inputValue: collateralUiAmount,
        inputMetadata: collateral?.metadata,
        onInputChange: handleInputChange,
        disabledInput,
        topLevelTotalPrincipalDue: totalPrincipalDue,
        principalMetadata: loanExpanded?.principalMetadata,
        totalDueInBalanceDisplay: totalDueInCollateral ?? 0,
        repaymentAmount: effectiveRepaymentAmount,
        loading,
        balance: collateral?.amount ?? 0,
        balanceMetadata: collateral?.metadata,
        balanceLoading: fullRepayWithCollateralQuoteLoading,
        balanceError: fullRepayWithCollateralQuoteError,
        error,
        ctaError,
        onRepay: repay,
        onSetMax: setMaxRepay,
        tokenRestrictionConfig: tokenRestrictionsConfig,
        loanExpanded,
        repayType: WindType.Collateral as const,
        repayLabel: "Repay with",
        newCollateralAmount: newCollateralAmount,
        newPrincipalAmount: (totalPrincipalDue ?? 0) - (effectiveRepaymentAmount ?? 0),
        isFullRepay: isFullRepay,
        executionDetails: executionDetails
    };
}

function getLabelOverridesForCollateralRepayment(): Partial<ExecutionDetailsLabels> {
    return {
        executionImpact: {
            caption: "Execution impact",
            tooltip: `Combined worst-case impact of slippage, fees, price impact, and market price for swapping loan collateral to repay principal`
        },
        fairMarketValue: {
            caption: "Fair market value",
            tooltip: `The fair market oracle value of principal from swapped collateral`
        },
        oracleSpread: {
            caption: "Oracle spread",
            tooltip: `Adjustment due to difference in oracle price and swap price`
        },
        receiveOnClose: {
            caption: "Principal Repaid",
            tooltip: `The minimum amount of repaid principal after swapping position collateral`
        }
    };
}
